import React from "react";
import { Link, Navigate } from "react-router-dom";
import { Check } from "lucide-react";
import { getPracticeArea } from "../data/practiceAreas";
import ContactForm from "../components/site/ContactForm";

export default function PracticeArea({ slug }) {
  const area = getPracticeArea(slug);

  if (!area) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      {/* Breadcrumb + Hero */}
      <section className="bg-white border-b border-hairline" data-testid="practice-hero">
        <div className="max-w-content mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <nav className="text-sm text-subink mb-5">
            <Link to="/" className="hover:text-gold">Home</Link>
            <span className="mx-2 text-hairline">/</span>
            <a href="/#practice-areas" className="hover:text-gold">Practice Areas</a>
            <span className="mx-2 text-hairline">/</span>
            <span className="text-ink">{area.name}</span>
          </nav>
          <p className="text-gold font-semibold text-sm uppercase tracking-wider mb-3">
            {area.audience === "tax-advisory"
              ? "For Tax Professionals & Advisory Firms"
              : "Exclusive Legal Leads"}
          </p>
          <h1 className="text-4xl sm:text-5xl max-w-3xl">{area.h1}</h1>
        </div>
      </section>

      {/* Body copy */}
      <section className="bg-white" data-testid="practice-body">
        <div className="max-w-content mx-auto px-4 sm:px-6 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
            <div className="lg:col-span-2 space-y-5">
              {area.paragraphs.map((p, i) => (
                <p key={i} className="text-ink text-[17px] leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
            {area.image && (
              <div className="lg:col-span-1">
                <img
                  src={area.image}
                  alt={area.name}
                  className="w-full h-64 lg:h-full lg:max-h-[360px] object-cover rounded-[4px] border border-hairline"
                  data-testid="practice-image"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* What we screen for */}
      <section className="bg-altbg" data-testid="practice-screen">
        <div className="max-w-content mx-auto px-4 sm:px-6 py-12">
          <h2 className="text-2xl sm:text-3xl mb-6">What We Screen For</h2>
          <ul className="space-y-3 max-w-2xl">
            {area.screenFor.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                <span className="text-ink">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Contact form */}
      <section className="bg-white" data-testid="practice-contact">
        <div className="max-w-content mx-auto px-4 sm:px-6 py-14">
          <h2 className="text-2xl sm:text-3xl mb-3">
            Request {area.name} Leads
          </h2>
          <p className="text-subink max-w-2xl mb-8">
            Tell us about your {area.audience === "tax-advisory" ? "advisory practice" : "firm"} and
            we'll follow up with pricing and current availability.
          </p>
          <div className="max-w-3xl">
            <ContactForm sourcePage={area.slug} defaultArea={area.name} />
          </div>
        </div>
      </section>
    </>
  );
}
